import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

export const leadService = {
  // Get all leads (admin)
  async getMyLeads() {
    const { data, error } = await supabase
      ?.from('customer_leads')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Subscribe to real-time lead changes
  async subscribeToLeads(callback) {
    const channel = supabase
      ?.channel('customer_leads_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'customer_leads' }, async () => {
        const { data } = await supabase
          ?.from('customer_leads')
          ?.select('*')
          ?.order('created_at', { ascending: false });
        callback(toCamelCase(data || []));
      })
      ?.subscribe();
    return { unsubscribe: () => supabase?.removeChannel(channel) };
  },

  // Update lead status
  async updateStatus(id, status) {
    const updateData = {
      status,
      updated_at: new Date()?.toISOString(),
      ...(status === 'contacted' && { contacted_date: new Date()?.toISOString() }),
      ...(status === 'qualified' && { qualified_date: new Date()?.toISOString() }),
      ...(status === 'converted' && { converted_date: new Date()?.toISOString() }),
      ...(status === 'closed' && { closed_date: new Date()?.toISOString() }),
    };
    const { data, error } = await supabase
      ?.from('customer_leads')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Create lead (public - from enquiry form)
  // Maps camelCase form fields to the exact DB column names
  async create(leadData) {
    const {
      name,
      email,
      phone,
      company,
      serviceRequired,
      message,
      source,
      status,
      budget,
      timeline,
      // strip unknown columns (e.g. country) that don't exist in the table
      country: _country,
      ...rest
    } = leadData || {};

    const customerName = name || rest?.customerName || '';
    const customerEmail = email || rest?.customerEmail || '';
    const customerPhone = phone || rest?.customerPhone || null;

    // Try SECURITY DEFINER RPC function first (bypasses RLS reliably)
    try {
      const { data: rpcData, error: rpcError } = await supabase
        ?.rpc('insert_customer_lead', {
          p_customer_name: customerName,
          p_customer_email: customerEmail,
          p_customer_phone: customerPhone,
          p_message: message || '',
          p_company: company || null,
          p_service_required: serviceRequired || null,
          p_source: source || null,
          p_budget: budget || null,
          p_timeline: timeline || null,
          p_name: name || null,
          p_email: email || null,
          p_phone: phone || null,
        });

      if (!rpcError && rpcData !== null && rpcData !== undefined) {
        // RPC returns UUID directly
        return { id: rpcData };
      }
      if (rpcError) {
        console.warn('RPC insert_customer_lead failed, trying direct insert:', rpcError?.message);
      }
    } catch (rpcErr) {
      console.warn('RPC call exception, trying direct insert:', rpcErr);
    }

    // Fallback: direct insert
    const insertPayload = {
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      message: message || '',
      company: company || null,
      service_required: serviceRequired || null,
      source: source || null,
      status: status || 'new',
      budget: budget || null,
      timeline: timeline || null,
      name: name || null,
      email: email || null,
      phone: phone || null,
    };

    const { data, error } = await supabase
      ?.from('customer_leads')
      ?.insert([insertPayload])
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Alias used by LeadGenerationTool
  async createLead(leadData) {
    return this.create(leadData);
  },
};